import { useState, useEffect } from 'react';
import { getWeek } from '../components/performance-components/week_number.js';
import { DEFAULT_VALUES, getRightLayer } from '../config/performance';
import { formatDateForCql, getDateRange, combineDateAndTime } from '../utils/dateUtils';

/**
 * Custom hook for managing performance view state
 * @returns {Object} State values and handlers
 */
export const usePerformanceState = () => {
  // Core state
  const [cqlSevere, setCqlSevere] = useState(DEFAULT_VALUES.CQL_SEVERE);
  const [cqlFecha, setCqlFecha] = useState(DEFAULT_VALUES.CQL_FECHA);
  const [mapLeftLayer, setMapLeftLayer] = useState(DEFAULT_VALUES.MAP_LEFT_LAYER);
  const [mapRightLayer, setMapRightLayer] = useState(null);
  
  // Button states
  const [selectedModel, setSelectedModel] = useState(DEFAULT_VALUES.SELECTED_MODEL);
  const [selectedRisk, setSelectedRisk] = useState(DEFAULT_VALUES.CQL_SEVERE);
  const [issueTime, setIssueTime] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [issueTimeOptions, setIssueTimeOptions] = useState([]);
  
  // UI state
  const [tabIndex, setTabIndex] = useState(DEFAULT_VALUES.TAB_INDEX);
  const [backdropOpen, setBackdropOpen] = useState(false);

  // Computed values
  const { min: cqlFechaMin, max: cqlFechaMax } = getDateRange(cqlFecha);
  const weekNumber = getWeek(new Date(cqlFecha));

  // Update right layer when dependencies change
  useEffect(() => {
    const rightLayer = getRightLayer(mapLeftLayer, cqlSevere);
    setMapRightLayer(rightLayer);
  }, [mapLeftLayer, cqlSevere, weekNumber]);

  // Combine date and time for issue time
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const combinedDateTime = combineDateAndTime(selectedDate, selectedTime);
      setIssueTime(combinedDateTime);
    }
  }, [selectedDate, selectedTime]);

  // Event handlers
  const handleDateChange = (fecha) => {
    const formattedDate = formatDateForCql(fecha);
    setCqlFecha(formattedDate);
  };

  const handleModelClick = (value) => {
    setSelectedModel(value);
    setMapLeftLayer(value);
  };

  const handleRiskClick = (value) => {
    setSelectedRisk(value);
    setCqlSevere(value);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };

  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  return {
    // State values
    cqlSevere,
    cqlFecha,
    cqlFechaMin,
    cqlFechaMax,
    weekNumber,
    mapLeftLayer,
    mapRightLayer,
    selectedModel,
    selectedRisk,
    issueTime,
    selectedDate,
    selectedTime,
    issueTimeOptions,
    tabIndex,
    backdropOpen,
    
    // State setters
    setCqlSevere,
    setCqlFecha,
    setSelectedModel,
    setSelectedRisk,
    setIssueTime,
    setSelectedDate,
    setSelectedTime,
    setIssueTimeOptions,
    setTabIndex,
    setBackdropOpen,
    
    // Event handlers
    handleDateChange,
    handleModelClick,
    handleRiskClick,
    handleDateSelect,
    handleTimeSelect,
    handleTabChange,
    handleBackdropOpen,
    handleBackdropClose,
  };
};
